<template>
  <main class="backup-warn-page">
    <AppNavBar :border="false" background-color="#f5f5f5" />

    <section class="backup-warn-body">
      <h1>{{ t('auth.backupWarnTitle') }}</h1>

      <div class="tip-row">
        <div v-for="item in tipItems" :key="item.key" class="tip-item">
          <app-image :src="item.ok ? circleYesIcon : circleXIcon" alt="" />
          <span>{{ item.text }}</span>
        </div>
      </div>

      <div class="warn-list">
        <article v-for="item in warnItems" :key="item.title" class="warn-item">
          <app-image :src="item.icon" alt="" />
          <div>
            <div data-strong>{{ item.title }}</div>
            <p>{{ item.desc }}</p>
          </div>
        </article>
      </div>
    </section>

    <footer class="backup-warn-footer">
      <button type="button" class="check-row" @click="isChecked = !isChecked">
        <van-icon :name="isChecked ? 'checked' : 'circle'" :color="isChecked ? '#2667ff' : '#00B42A'" size="16" />
        <span>{{ t('auth.backupWarnCheck') }}</span>
      </button>
      <AuthThemeButton block :disabled="!isChecked" @click="startBackup">
        {{ t('auth.backupMnemonic') }}
      </AuthThemeButton>
    </footer>

    <PayPasswordPopup
      v-model:show="showPasswordDialog"
      v-model:password="passwordInput"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :forgot-text="t('auth.forgotPasswordRecovery')"
      :show-forgot="true"
      :confirm-disabled="!passwordInput.trim()"
      @closed="handlePasswordClosed"
      @forgot="handleForgotPassword"
      @confirm="confirmPassword"
    />
  </main>
</template>

<script setup>
import { ref } from 'vue';
import { showFailToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import PayPasswordPopup from '@/components/PayPasswordPopup.vue';
import AuthThemeButton from '@/view/auth/components/AuthThemeButton.vue';
import { useProfileStore } from '@/stores/profile';
import { navigateWithState } from '@/utils/route-state';
import { getRecoverableMnemonic, getWalletPassword } from '@/utils/walletSession';
import circleYesIcon from '@/assets/images/circle_yes_icon.png';
import circleXIcon from '@/assets/images/circle_x_icon.png';
import lockIcon from '@/assets/images/loginImage/lock_icon.png';
import editIcon from '@/assets/images/settingImage/edit_icon.png';
import protectIcon from '@/assets/images/settingImage/protect_icon.png';

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const isChecked = ref(true);
const showPasswordDialog = ref(false);
const passwordInput = ref('');

const tipItems = [
  { key: 'ok', ok: true, text: t('auth.backupTipWrite') },
  { key: 'no-copy', ok: false, text: t('auth.backupTipNoCopy') },
  { key: 'no-screenshot', ok: false, text: t('auth.backupTipNoScreenshot') }
];

const warnItems = [
  { icon: lockIcon, title: t('auth.backupWarn1Title'), desc: t('auth.backupWarn1Desc') },
  { icon: editIcon, title: t('auth.backupWarn2Title'), desc: t('auth.backupWarn2Desc') },
  { icon: protectIcon, title: t('auth.backupWarn3Title'), desc: t('auth.backupWarn3Desc') }
];

function proceedBackup() {
  navigateWithState(router, '/auth/backup-mnemonic', {
    namespace: 'auth-backup-mnemonic',
    payload: {
      export: '1'
    }
  });
}

function startBackup() {
  const address = profileStore.accountStorageAddress;
  const mnemonic = getRecoverableMnemonic(address);
  if (!mnemonic) {
    showFailToast(t('auth.mnemonicUnavailable'));
    return;
  }

  const savedPassword = getWalletPassword(address);
  if (!savedPassword) {
    proceedBackup();
    return;
  }

  passwordInput.value = '';
  showPasswordDialog.value = true;
}

function confirmPassword() {
  const savedPassword = getWalletPassword(profileStore.accountStorageAddress);
  if (passwordInput.value !== savedPassword) {
    showFailToast(t('auth.passwordMismatch'));
    return;
  }
  showPasswordDialog.value = false;
  proceedBackup();
}

function handlePasswordClosed() {
  passwordInput.value = '';
}

function handleForgotPassword() {
  showPasswordDialog.value = false;
  passwordInput.value = '';
  navigateWithState(router, '/auth/forgot-pay-password', {
    namespace: 'auth-forgot-pay-password',
    payload: {
      back: '/auth/backup-warn'
    }
  });
}
</script>

<style scoped lang="scss">
.backup-warn-page {
  display: flex;
  flex-direction: column;
  min-height: var(--app-viewport-height);
  background: #f5f5f5;
}

.backup-warn-body {
  flex: 1;
  padding: 0 14px;

  h1 {
    margin: 0;
    color: #000000;
    font-size: 22px;
    font-weight: 700;
  }
}

.tip-row {
  display: flex;
  gap: 8px;
  margin-top: 21px;
}

.tip-item {
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;

  .app-image {
    width: 14px;
    height: 14px;
    margin-right: 5px;
    flex-shrink: 0;
  }

  span {
    color: #666666;
    font-size: 12px;
    line-height: 1.4;
  }
}

.warn-list {
  margin-top: 24px;
}

.warn-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;

  .app-image {
    width: 20px;
    height: 20px;
    margin-right: 12px;
    flex-shrink: 0;
  }

  [data-strong] {
    display: block;
    color: #222222;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
  }

  p {
    margin: 5px 0 0;
    color: #666666;
    font-size: 12px;
    line-height: 1.5;
  }
}

.backup-warn-footer {
  padding: 0 14px 30px;
}

.check-row {
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0 0 16px;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;

  span {
    margin-left: 5px;
    color: #383838;
    font-size: 12px;
    line-height: 1.5;
  }
}

</style>
